import React, { useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  Paper,
  Popper,
  Tooltip,
  useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faTriangleExclamation,
} from '@fortawesome/pro-regular-svg-icons';
import { InputLabelBase } from '../../../../../../../components/inputs/InputLabelBase';
import type { CarrierEligibility } from '../../../../../../../api/quotes.api';
import {
  deriveCarrierStatus,
  deriveCarrierWarning,
} from '../carrierEligibilityDisplay';
import CarrierEligibilityList from './CarrierEligibilityList';
import {
  SELECT_CONTENT_HEIGHT,
  SELECT_INPUT_PADDING,
} from '../../../../../../../themes/_base/_base.overrides';

interface CarrierSelectProps {
  readonly options: CarrierEligibility[];
  readonly value: string | number;
  readonly onSelect: (treatyId: string) => void;
  readonly disabled?: boolean;
  /**
   * Rendered beside the "Carrier" label, which this component owns.
   *
   * The staged accept/reject pair, in practice. The carrier is the one global
   * that is not a `GlobalInput`, so without this it was the one global whose
   * staged change could not be resolved in place.
   */
  readonly labelAdornment?: React.ReactNode;
  /** Merged into the "Carrier" label's style. */
  readonly labelStyle?: React.CSSProperties;
}

/**
 * MUI carrier selector for the standard (non-excess) rater header (FPX-7027). A
 * boxed trigger that fits the rater grid, opening the shared eligibility panel.
 * Uses a Popper (not a modal Popover) so the page still scrolls behind it, with a
 * ClickAwayListener to close. Mirrors the Mantine CarrierPill on the excess header.
 */
const CarrierSelect = ({
  options,
  value,
  onSelect,
  disabled,
  labelAdornment,
  labelStyle,
}: CarrierSelectProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dark = useTheme().palette.mode === 'dark';
  const selectedValue = String(value);
  const selected = options.find((option) => option.treatyId === selectedValue);
  const fallbackLabel = options.length === 0 ? 'Loading…' : selectedValue;
  const selectedLabel = selected?.carrierName ?? fallbackLabel;
  const selectedStatus = selected ? deriveCarrierStatus(selected) : undefined;
  const isDeviation = selected != null && !selected.isAssigned;
  const warning = deriveCarrierWarning(selectedStatus, isDeviation);

  const handleSelect = (treatyId: string) => {
    onSelect(treatyId);
    setOpen(false);
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    // The trigger toggles itself; ignore click-aways that land on it so a toggle
    // click doesn't close-then-reopen.
    if (triggerRef.current?.contains(event.target as Node)) {
      return;
    }
    setOpen(false);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          paddingBottom: '0.25rem',
        }}
      >
        <InputLabelBase
          style={{
            padding: 0,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            ...labelStyle,
          }}
        >
          Carrier
        </InputLabelBase>
        {labelAdornment}
        {warning ? (
          <Tooltip title={warning.label} placement="bottom">
            <span
              data-testid={warning.testId}
              style={{
                display: 'inline-flex',
                color: warning.color,
                cursor: 'help',
                fontSize: 12,
              }}
            >
              <FontAwesomeIcon icon={faTriangleExclamation} />
            </span>
          </Tooltip>
        ) : null}
      </Box>
      <Box
        component="button"
        type="button"
        ref={triggerRef}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          display: 'flex',
          width: '100%',
          minWidth: 0,
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderRadius: '5px',
          // A Select's padding and content height, so Carrier stands exactly as
          // tall as the Selects in the same row.
          padding: `${SELECT_INPUT_PADDING} 0.6666666667rem`,
          border: 'none',
          textAlign: 'left',
          font: 'inherit',
          color: 'inherit',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.5 : 1,
          backgroundColor: (theme) => theme.palette.background.active,
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: SELECT_CONTENT_HEIGHT,
          }}
        >
          {selectedLabel}
        </span>
        <FontAwesomeIcon
          icon={faChevronDown}
          style={{ fontSize: 12, opacity: 0.6, flexShrink: 0 }}
        />
      </Box>
      <Popper
        open={open}
        anchorEl={triggerRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Paper elevation={8} sx={{ mt: 0.5, p: 0.75, borderRadius: 2 }}>
            <CarrierEligibilityList
              options={options}
              selectedValue={selectedValue}
              onSelect={handleSelect}
              dark={dark}
            />
          </Paper>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

export default CarrierSelect;
